import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Sparkles, ArrowRight, Send } from 'lucide-react';
import { Link } from 'react-router-dom';
import { HERO_SLIDES } from '../data/mock';
import PromoBanners from './PromoBanners';

const HeroSlider = () => {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % HERO_SLIDES.length), 6000);
    return () => clearInterval(t);
  }, []);

  const next = () => setIdx(i => (i + 1) % HERO_SLIDES.length);
  const prev = () => setIdx(i => (i - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);

  return (
    <section className="relative overflow-hidden bg-slate-950">
      {/* Slides */}
      <div className="relative h-[440px] sm:h-[480px] lg:h-[520px]">
        {HERO_SLIDES.map((slide, i) => {
          const Heading = i === 0 ? 'h1' : 'h2';
          return (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${i === idx ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
          >
            {/* Background image */}
            <img
              src={slide.image}
              alt={slide.title}
              className={`absolute inset-0 w-full h-full object-cover ${i === idx ? 'animate-[heroZoom_8s_ease-out_forwards]' : ''}`}
              onError={(event) => { event.currentTarget.style.display = 'none'; }}
            />
            {/* Dark gradient overlay for text readability */}
            <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/65 to-slate-950/30" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent" />

            {/* Content */}
            <div className="relative h-full max-w-7xl mx-auto px-4 flex items-center">
              <div className={`max-w-2xl ${i === idx ? 'animate-[fadeIn_0.9s_ease-out]' : ''}`}>
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-500/20 backdrop-blur-sm border border-blue-400/30 text-blue-200 text-xs font-semibold mb-5">
                  <Sparkles className="w-3.5 h-3.5" /> {slide.accent}
                </div>
                <Heading className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight mb-5 drop-shadow-2xl">
                  {slide.title}
                </Heading>
                <p className="text-base sm:text-lg text-blue-50/90 mb-7 max-w-lg leading-relaxed">
                  {slide.subtitle}
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link to="/sklep" className="group inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-7 py-3.5 rounded-full font-semibold shadow-xl shadow-blue-900/40 transition">
                    {slide.cta} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <a href="https://t.me/+9A5CpRihCfNjMzc0" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md hover:bg-white/20 border border-white/30 text-white px-7 py-3.5 rounded-full font-semibold transition">
                    <Send className="w-4 h-4" /> Telegram
                  </a>
                </div>
              </div>
            </div>
          </div>
          );
        })}

        {/* Controls */}
        <div className="absolute bottom-5 left-0 right-0 z-20 max-w-7xl mx-auto px-4 flex items-center justify-between">
          <div className="flex gap-2">
            {HERO_SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-12 bg-blue-400' : 'w-6 bg-white/40 hover:bg-white/60'}`}
                aria-label={`Slide ${i+1}`}
              />
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={prev} className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 hover:border-white/40 flex items-center justify-center transition" aria-label="Previous">
              <ChevronLeft className="w-4 h-4 text-white" />
            </button>
            <button onClick={next} className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 hover:border-white/40 flex items-center justify-center transition" aria-label="Next">
              <ChevronRight className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>
      </div>

      {/* Promo banners auto-slider (between hero and stats) */}
      <PromoBanners />

      {/* Stats strip below slider */}
      <div className="bg-white border-b border-blue-100">
        <div className="max-w-7xl mx-auto px-4 py-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="flex items-center justify-center sm:justify-start gap-2">
            <div className="text-2xl font-bold text-slate-900">15K+</div>
            <div className="text-xs text-slate-500 leading-tight">Zadowolonych<br/>klientów</div>
          </div>
          <div className="flex items-center justify-center sm:justify-start gap-2 sm:border-l sm:border-blue-100 sm:pl-4">
            <div className="text-2xl font-bold text-slate-900">48h</div>
            <div className="text-xs text-slate-500 leading-tight">Szybka<br/>wysyłka</div>
          </div>
          <div className="flex items-center justify-center sm:justify-start gap-2 sm:border-l sm:border-blue-100 sm:pl-4">
            <div className="text-2xl font-bold text-slate-900">100%</div>
            <div className="text-xs text-slate-500 leading-tight">Pełna<br/>anonimowość</div>
          </div>
          <div className="flex items-center justify-center sm:justify-start gap-2 sm:border-l sm:border-blue-100 sm:pl-4">
            <div className="text-2xl font-bold text-slate-900">5★</div>
            <div className="text-xs text-slate-500 leading-tight">Średnia ocena<br/>klientów</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSlider;
